import React from 'react'

import ReCAPTCHA from 'react-google-recaptcha'
import { Trans, useTranslation } from 'react-i18next'

import facebookLogo from '@assets/images/FacebookLogo.png'
import googleLogo from '@assets/images/GoogleLogo.png'
import useCreateAccountForm from '@components/music/title-details/purchase-title-dialog/SignInRequired/hooks/useCreateAccountForm'
import Button from '@components/shared/Button'
import ExternalLink from '@components/shared/ExternalLink'
import Checkbox from '@components/shared/form/Checkbox'
import FieldError from '@components/shared/form/FieldError'
import Input from '@components/shared/form/Input'
import Tippy from '@tippyjs/react'
import { GOOGLE_SITE_KEY, ThemeType } from '@utils/global'
import PageRoutes from '@utils/page-routes'
import Storage from '@utils/storage'

interface Props {
  purchaseQuantity: number
  titleId: string
}

const CreateAccountForm: React.FC<Props> = ({ purchaseQuantity, titleId }: Props) => {
  const { t } = useTranslation()

  const {
    data,
    updateData,
    errors,
    signUpWithGoogle,
    signUpWithFacebook,
    signUp,
    submitting,
    clearError,
  } = useCreateAccountForm(purchaseQuantity, titleId)

  return (
    <>
      <h1 className="mb-2 font-semibold text-center text-h3">
        {t('content.createaNoteflightAccount')}
      </h1>
      <div className="flex flex-col items-center w-full">
        {/* Terms Checkbox */}
        <div className="relative mb-4">
          <Checkbox
            name="terms"
            onChecked={(checked) => {
              updateData({ terms: checked })
              clearError('terms')
            }}
            className="relative"
            label={<CheckboxLabel />}
          />
          {!!errors.terms && (
            <div className="absolute z-50 px-2 py-1 text-white rounded top-full bg-orange">
              {errors.terms}
            </div>
          )}
        </div>

        <div className="flex flex-col items-center justify-between w-full sm:items-start sm:flex-row">
          {/* Sign Up Form */}
          <div className="flex flex-col flex-1">
            <div className="flex flex-row w-full">
              {/* Email */}
              <Input
                name="email"
                label={t('content.signup.email')}
                className="flex-1 mr-2.5"
                type="email"
                required
                value={data.email ?? ''}
                error={errors.email}
                solidBackground
                onChange={(e) => updateData({ email: e.target.value })}
              />

              {/* Age */}
              <Input
                name="age"
                label={
                  <span className="flex flex-row items-center">
                    {t('content.signup.age')}
                    <Tippy content={t('content.signup.ageInfo')}>
                      <i className="ml-1 nf-icon nf-info-sign text-gray-icons dark:text-gray-icons-dm" />
                    </Tippy>
                  </span>
                }
                type="number"
                min={1}
                required
                value={data.age ?? ''}
                onChange={(e) => updateData({ age: e.target.value })}
                error={errors.age}
                className="w-20"
                solidBackground
              />
            </div>

            {/* Username */}
            <Input
              name="username"
              label={t('content.username')}
              required
              value={data.username ?? ''}
              error={errors.username}
              onChange={(e) => updateData({ username: e.target.value })}
              solidBackground
            />

            {/* Password */}
            <Input
              name="password"
              label={t('content.password')}
              type="password"
              required
              value={data.password ?? ''}
              error={errors.password}
              onChange={(e) => updateData({ password: e.target.value })}
              solidBackground
            />

            {/* Recaptcha */}
            <div className="self-center my-2">
              <ReCAPTCHA
                sitekey={GOOGLE_SITE_KEY}
                onChange={(value) => updateData({ captchaToken: value })}
                theme={Storage.theme === ThemeType.DARK ? 'dark' : 'light'}
                size="normal"
              />
              {errors.captchaToken && <FieldError error={t(errors.captchaToken)} />}
            </div>

            <Button onClick={signUp} disabled={submitting}>
              {t('action.signUp')}
            </Button>
          </div>

          {/* Or Separator */}
          <span className="text-sm mx-6 opacity-75 mt-6 sm:mt-[5.5rem]">
            {t('content.orCapital')}
          </span>

          {/* OAuth Sign Up */}
          <div className="flex flex-col mt-6 sm:mt-12">
            <Button
              variant="outline"
              className="flex flex-row items-center w-full my-2"
              onClick={signUpWithGoogle}>
              <img src={googleLogo} alt="Google" className="w-5 mr-2" />
              <span className="text-md">{t('content.signupWithGoogle')}</span>
            </Button>
            <Button
              variant="outline"
              className="flex flex-row items-center w-full my-2"
              onClick={signUpWithFacebook}>
              <img src={facebookLogo} alt="Facebook" className="w-5 mr-2" />
              <span className="text-md">{t('content.signupWithFacebook')}</span>
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

export default CreateAccountForm

function CheckboxLabel(): JSX.Element {
  return (
    <Trans i18nKey="content.consentToTermsOfUse">
      I agree to Noteflight's
      <ExternalLink
        className="orange-link"
        onClick={(e) => {
          // This allows the link to be clicked instead of the checkbox
          e.stopPropagation()
        }}
        href={PageRoutes.legalTermsPath()}>
        Terms of Use
      </ExternalLink>
      and
      <ExternalLink
        className="orange-link"
        onClick={(e) => {
          // This allows the link to be clicked instead of the checkbox
          e.stopPropagation()
        }}
        href={PageRoutes.legalPrivacyPath()}>
        Privacy Policy
      </ExternalLink>
    </Trans>
  )
}
